import type { InsertableOptions } from '@aceable/core';
import { InsertableModule } from '@aceable/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import type { AfterViewInit, ComponentFactory, ComponentRef, OnInit, Type } from '@angular/core';
import {
  ApplicationRef,
  ChangeDetectorRef,
  Compiler,
  Component,
  ElementRef,
  inject,
  Inject,
  Injector,
  Input,
  makeStateKey,
  PLATFORM_ID,
  Renderer2,
  TransferState,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { filter, first } from 'rxjs';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { GraphicBannerHideOnSaleDirective } from '@core-mkt/directives/graphic-banner-hide-on-sale.directive';
import { ShortcodeRendererDirective } from '@core-mkt/directives/shortcode-renderer.directive';
import type { CraftData, PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { AdobeAnalyticService } from '@core-mkt/services/adobe-analytics/adobe-analytics.service';
import { MktComponentRegistryService } from '@core-mkt/services/dynamic-component/single-template-component-registry.service';
import { EmbedStylesService } from '@core-mkt/services/embed-styles/embed-styles.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { GeolocationService } from '@core-mkt/services/geolocation/geolocation.service';
import { HeaderOverrideService } from '@core-mkt/services/header-override/header-override.service';
import { MetaTrackingService } from '@core-mkt/services/meta/meta-tracking.service';
import { AnalyticsConsentService } from '@core-mkt/services/analytics/analytics-consent.service';
import { PrivacyCheckService } from '@core-mkt/services/privacy-check/privacy-check.service';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';
import { getBrandSiteUrl, getOrganizationFallbackSchema } from '@core-mkt/services/schema/organization.schema';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { SeoDataService } from '@core-mkt/services/seo-data/seo-data.service';
import { WistiaService } from '@core-mkt/services/wistia/wistia.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { SeoData } from '@core-mkt/services/seo-data/seo-data';
import type { EmailCapture } from '@core-mkt/shared/components/email-capture/email-capture/email-capture';
import type { ExitIntentModal } from '@core-mkt/shared/components/exit-intent-modal/exit-intent-modal/exit-intent-modal';
import type { PhoneCapture } from '@core-mkt/shared/components/phone-capture/phone-capture/phone-capture';
import { logError } from '@core-mkt/shared/lib/logger';
import { cloneDeep } from 'lodash-es';

import { AmbassadorModalComponent } from '@core-mkt/shared/components/ambassador-modal/ambassador-modal/ambassador-modal.component';
import { EmailCaptureComponent } from '@core-mkt/shared/components/email-capture/email-capture/email-capture.component';
import { ExitIntentModalComponent } from '@core-mkt/shared/components/exit-intent-modal/exit-intent-modal/exit-intent-modal.component';
import { MarketingComponent } from '@core-mkt/shared/components/marketing/marketing/marketing.component';
import { PhoneCaptureComponent } from '@core-mkt/shared/components/phone-capture/phone-capture/phone-capture.component';
import { ReferAFriendContainerComponent } from '@core-mkt/shared/components/refer-a-friend-container/refer-a-friend-container/refer-a-friend-container.component';
import type { ReferAFriendGlobalConfig } from '@core-mkt/shared/components/refer-a-friend-container/refer-a-friend-container/refer-a-friend-container';
import { VisionModalComponent } from '@core-mkt/shared/components/vision-modal/vision-modal/vision-modal.component';
import { WebinarScheduleComponent } from '@core-mkt/shared/components/webinar-schedule/webinar-schedule.component';

const HEAD_PLACEHOLDER_KEY = makeStateKey<string>('headPlaceholder');

/**
 * A single Craft section rendered into the page body. Each section carries a
 * `typeHandle` (mapped to the registered component className) and is tagged with
 * its position via `index` before being pushed into `formattedData`.
 */
interface BaseComponentSection {
  typeHandle: string;
  index?: number;
  [key: string]: unknown;
}

/**
 * Craft payload consumed by this template. Extends the shared {@link CraftData}
 * with the fields this template reads that are not (yet) declared on the shared
 * interface: `entryData.baseComponents`, `stickyEmailCapture.phoneCapture`,
 * `optimizely.optimizelyEnabled`, `globalOptimizely.globalOptimizely`, and
 * `exitIntentModal`.
 */
interface TemplateSingleCraftData extends CraftData {
  entryData: NonNullable<CraftData['entryData']> & { baseComponents: BaseComponentSection[] };
  styles: NonNullable<CraftData['styles']>;
  optimizely: { optimizelyEnabled: boolean };
  headerData?: NonNullable<CraftData['headerData']> & { stickyEmailOverride?: number };
  stickyEmailCapture?: NonNullable<CraftData['stickyEmailCapture']> & { phoneCapture?: PhoneCapture[] };
  globalOptimizely?: { globalOptimizely?: boolean };
  exitIntentModal?: ExitIntentModal;
}

/**
 * Module classes lazily imported for header/banner/footer rendering expose a
 * `resolveComponent()` factory used to instantiate the brand component.
 */
interface ResolvableModule {
  resolveComponent(): ComponentFactory<unknown>;
}

@Component({
  selector: 'template-single',
  templateUrl: './template-single.component.html',
  imports: [
    AmbassadorModalComponent,
    EmailCaptureComponent,
    PhoneCaptureComponent,
    ExitIntentModalComponent,
    WebinarScheduleComponent,
    VisionModalComponent,
    ReferAFriendContainerComponent,
    MarketingComponent,
    InsertableModule,
    ShortcodeRendererDirective,
    GraphicBannerHideOnSaleDirective,
  ],
  standalone: true,
})
export class TemplateSingleComponent implements AfterViewInit, OnInit {
  @Input() segmentIdOverride?: string;
  @ViewChild('head', { read: ViewContainerRef })
  public head: ViewContainerRef;
  @ViewChild('foot', { read: ViewContainerRef })
  public foot: ViewContainerRef;
  resolverData: PageDataResponse;
  pageData: TemplateSingleCraftData;
  formattedData: InsertableOptions[];
  h1Override: string;
  includeOptimizely: boolean;
  globalOptimizelyFlag: boolean;
  showAmbassadorModal: boolean;
  currentBrand: string;
  emailCapture?: EmailCapture;
  phoneCapture?: PhoneCapture;
  referAFriendConfig?: ReferAFriendGlobalConfig;
  stickyEmailOverride?: number;
  exitIntentConfig?: ExitIntentModal;
  showHeadPlaceholder = false;
  headPlaceholderHtml: SafeHtml = '';
  private headComponentRefs: ComponentRef<unknown>[] = [];
  private readonly analyticsConsentService = inject(AnalyticsConsentService);

  constructor(
    private route: ActivatedRoute,
    private embedStylesService: EmbedStylesService,
    private seoDataService: SeoDataService,
    private registryService: MktComponentRegistryService,
    private redactor: WysiwygRedactorService,
    private wistia: WistiaService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private headerOverrideService: HeaderOverrideService,
    private env: EnvService,
    private compiler: Compiler,
    private aarpAnalytics: AdobeAnalyticService,
    private injector: Injector,
    private geolocation: GeolocationService,
    private cdr: ChangeDetectorRef,
    public privacyCheckService: PrivacyCheckService,
    public metaTrackingService: MetaTrackingService,
    private queryParamService: QueryParamService,
    @Inject(PLATFORM_ID) private platformId: object,
    private schemaService: SchemaService,
    private appRef: ApplicationRef,
    private transferState: TransferState,
    private sanitizer: DomSanitizer,
  ) {
    this.geolocation.loadLocationCookie();

    this.formattedData = [];
    this.resolverData = cloneDeep(this.route.snapshot.data['pageData'] as PageDataResponse);
    // The shared CraftData covers most fields; this template also reads a few Craft
    // fields not declared there (baseComponents, phoneCapture, exitIntentModal, etc.).
    this.pageData = this.resolverData.craftData as unknown as TemplateSingleCraftData;
    this.emailCapture = this.pageData?.stickyEmailCapture?.emailCapture[0];
    this.phoneCapture = this.pageData?.stickyEmailCapture?.phoneCapture?.[0];
    this.referAFriendConfig = this.pageData?.referAFriendConfig;
    this.stickyEmailOverride = this.pageData?.headerData?.stickyEmailOverride;
    this.showAmbassadorModal = this.pageData.entryData.slug === 'home';
    if (this.pageData.styles.embedStyles) {
      this.embedStylesService.addEmbedStyles(this.pageData.styles.embedStyles);
    }
    this.globalOptimizelyFlag = this.pageData?.globalOptimizely?.globalOptimizely ?? false;
    const adobeData = this.pageData.adobe;
    if (adobeData && this.env.get.adobeAnalytics.length > 0) {
      this.aarpAnalytics.initializeAnalytics(this.pageData);
    }
    this.includeOptimizely = this.pageData.optimizely.optimizelyEnabled || this.globalOptimizelyFlag;
    this.h1Override = this.queryParamService.getQueryParam('hlText')?.split('-').join(' ');
    this.pageData.entryData.baseComponents.forEach((section, index) => {
      if (Object.keys(section).length > 1) {
        section.index = index;
        this.formattedData.push({
          className: section.typeHandle,
          data: section,
        });
      }
    });

    // SeoContent (Craft resolver shape) and SeoData (service shape) describe the same
    // runtime SEO payload but are declared as separate, non-overlapping interfaces.
    this.seoDataService.setSeoProperties(this.pageData.seoContent as unknown as SeoData);

    // Restore SSR-rendered head HTML on the client to prevent header flicker during lazy module loading
    if (isPlatformBrowser(this.platformId)) {
      const ssrHeadHtml = this.transferState.get(HEAD_PLACEHOLDER_KEY, '');
      if (ssrHeadHtml) {
        this.headPlaceholderHtml = this.sanitizer.bypassSecurityTrustHtml(ssrHeadHtml);
        this.showHeadPlaceholder = true;
        this.transferState.remove(HEAD_PLACEHOLDER_KEY);
      }
    }

    // Initialize exit intent popup configuration
    this.exitIntentConfig = this.pageData?.exitIntentModal;
  }

  async ngOnInit(): Promise<void> {
    this.metaTrackingService.getMetaContext();
    const brand = this.env.get.brandConfig.id;
    this.currentBrand = brand;
    switch (brand) {
      case 'AA': {
        await import(
          /* webpackChunkName: "aa-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "aa-header-module" */
          '@core-mkt/brands/aa/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "aa-footer-module" */
          '@core-mkt/brands/aa/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'QA': {
        await import(
          /* webpackChunkName: "aa-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "aa-header-module" */
          '@core-mkt/brands/qa/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "aa-footer-module" */
          '@core-mkt/brands/qa/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'ACE': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "ace-header-module" */
          '@core-mkt/brands/ace/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "ace-footer-module" */
          '@core-mkt/brands/ace/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'AARP': {
        await import(
          /* webpackChunkName: "aarp-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "aarp-header-module" */
          '@core-mkt/brands/aarp/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "aarp-footer-module" */
          '@core-mkt/brands/aarp/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'DEC': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "dec-header-module" */
          '@core-mkt/brands/dec/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "dec-footer-module" */
          '@core-mkt/brands/dec/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'INS': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "ins-header-module" */
          '@core-mkt/brands/ins/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "ins-footer-module" */
          '@core-mkt/brands/ins/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'MTG': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "mtg-header-module" */
          '@core-mkt/brands/mtg/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "mtg-footer-module" */
          '@core-mkt/brands/mtg/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'HSP': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "hsp-header-module" */
          '@core-mkt/brands/hsp/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "hsp-footer-module" */
          '@core-mkt/brands/hsp/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }

      case 'IDS': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        )
          .then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'))
          .catch((error) => this.logModuleLoadError('BannerModule', error));
        await import(
          /* webpackChunkName: "ids-header-module" */
          '@core-mkt/brands/ids/src/app/components/header/header.module'
        )
          .then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'))
          .catch((error) => this.logModuleLoadError('HeaderModule', error));
        await import(
          /* webpackChunkName: "ids-footer-module" */
          '@core-mkt/brands/ids/src/app/components/footer/footer.module'
        )
          .then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'))
          .catch((error) => this.logModuleLoadError('FooterModule', error));
        break;
      }
    }

    // Server: capture rendered head HTML for TransferState (prevents client header flicker)
    this.captureHeadHtml();
    // Client: clear SSR placeholder now that real components are loaded
    this.clearHeadPlaceholder();

    const privacyCookie: string = this.privacyCheckService.getPrivacyCookie();

    // `!privacyCookie` deliberately treats an empty-valued cookie like a missing one (reset to the
    // default), even though resolveAdOptOut fails closed on ''. Nothing writes '' today.
    if (!privacyCookie) {
      // Default a visitor with no stored choice to 0 (allow), unless the browser sends Global
      // Privacy Control, which is a binding opt-out (1). Writing 0 for a GPC visitor would record
      // an "allow" they never gave.
      this.privacyCheckService.updatePrivacyCheck(this.analyticsConsentService.hasGlobalPrivacyControl() ? 1 : 0);
    }
  }

  ngAfterViewInit(): void {
    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first(),
      )
      .subscribe(() => {
        this.schemaService.finalize(this.buildOrganizationFallback());
      });

    this.headerOverrideService.overrideH1Tag(this.renderer, this.elementRef, this.h1Override);

    // Fire-and-forget: the iframe resize runs async and its result is not awaited here.
    void this.redactor.makeYouTubeIframeResponsive(this.renderer);

    this.redactor.updateLocation();

    // Wire up click handlers for popover video triggers after content is rendered
    setTimeout(() => {
      this.wistia.initializePopovers();
    }, 0);
  }

  /**
   * Builds the bare EducationalOrganization fallback for the universal `#organization` @id.
   * Components with richer Trustpilot data (top-section, trustpilot widget) register first;
   * first-write-wins keeps the rich node and the fallback is dropped at finalize-time.
   */
  private buildOrganizationFallback() {
    // Canonical brand site root — NOT document.URL. Deriving from the runtime
    // request URL produced per-environment org @ids (e.g. sandbox review-app
    // hosts) that fragmented the org into multiple nodes. See EPS-3951.
    const siteUrl = getBrandSiteUrl(this.env.get.brandConfig.url);
    const organizationName = this.env.get.brandConfig.name ?? '';
    return getOrganizationFallbackSchema({ siteUrl, organizationName });
  }

  /**
   * SSR-safe logging for lazy module load failures. Replaces the per-brand
   * `console.error` handlers so failures are captured by Coralogix.
   */
  private logModuleLoadError(moduleName: string, error: unknown): void {
    logError({
      error: error instanceof Error ? error : new Error(String(error)),
      message: 'TEMPLATE_SINGLE_MODULE_LOAD_FAILED',
      code: moduleName,
      tags: ['TEMPLATE', 'LAZY_MODULE'],
    });
  }

  private compileLazyModule(module: Type<unknown>, target: string): Promise<void> {
    return this.compiler.compileModuleAsync(module).then((moduleFactory) => {
      // Create a moduleRef, resolve an entry component, create the component
      const moduleRef = moduleFactory.create(this.injector);
      const componentFactory = (moduleRef.instance as ResolvableModule).resolveComponent();
      if (target === 'head') {
        const ref = this.head?.createComponent(componentFactory);
        if (ref) {
          this.headComponentRefs.push(ref);
        }
      } else {
        this.foot?.createComponent(componentFactory);
      }
    });
  }

  /**
   * On the server, capture the rendered head component HTML into TransferState
   * so the client can display it as a placeholder during lazy module loading.
   */
  private captureHeadHtml(): void {
    if (!isPlatformServer(this.platformId) || this.headComponentRefs.length === 0) {
      return;
    }
    // Trigger change detection so component templates are fully rendered before capturing
    this.headComponentRefs.forEach((ref) => ref.changeDetectorRef.detectChanges());
    const html = this.headComponentRefs.map((ref) => (ref.location.nativeElement as HTMLElement).outerHTML).join('');
    if (html) {
      this.transferState.set(HEAD_PLACEHOLDER_KEY, html);
    }
  }

  /**
   * On the client, remove the SSR placeholder once real components are ready.
   */
  private clearHeadPlaceholder(): void {
    this.showHeadPlaceholder = false;
    this.headPlaceholderHtml = '';
  }
}
